'use client';

import { c as _c } from "react/compiler-runtime";
import { css } from '@repo/pandacss/css';
import { AnalyticsFeatureViewed } from '@repo/platform-analytics/AnalyticsFeatureViewed';
import { useAnalyticsPageView } from '@repo/platform-analytics/AnalyticsPageView/Context';
import { useAnalyticsSkuViewedContext } from '@repo/platform-analytics/AnalyticsSkuViewed/Context';
import { useAnalytics } from '@repo/platform-analytics/hooks/useAnalytics';
import { usePageAction } from '@repo/platform-analytics/hooks/usePageAction';
import { useIdentityContext } from '@repo/platform-analytics/IdentityContext';
import { getCleanLocationHref } from '@repo/platform-analytics/lib/urlUtils';
import { auth0 } from '@repo/platform-auth0';
import { env } from '@repo/platform-env';
import { InfoTooltip } from '@repo/ui/composites/InfoTooltip';
import { Link } from '@repo/ui/Link';
import { Typography } from '@repo/ui/Typography';
import { useRef } from 'react';
import { v4 } from 'uuid';
import { CTA_LABEL, ENTITY_NAME, RECOMMENDED_CREDIT_LEARN_MORE_PATH, RECOMMENDED_CREDIT_TOOLTIP_TEXT, SECTION_NAME } from '@/features/approval-odds/constants';
import type { ApprovalOddsAnalytics } from './types';
export interface ApprovalOddsButtonProps {
  analytics?: ApprovalOddsAnalytics;
  hideTooltip?: boolean;
  firePageActionOnClick?: boolean;
}
const DASHBOARD_PATH = '/home/dashboard/home';
const SEGMENT_DEPLOYABLE_NAME = 'cardfinder-v2';
const approvalOddsButtonClass = css({
  color: 'blues.main',
  fontSize: '[0.8rem]',
  fontFamily: 'gotham',
  fontWeight: '[400]',
  textAlign: 'left',
  cursor: 'pointer',
  textTransform: 'none',
  borderWidth: '0',
  background: '[none]',
  px: '0',
  _hover: {
    textDecoration: 'underline'
  }
});
const ApprovalOddsButtonInner = ({
  analytics,
  firePageActionOnClick
}: {
  analytics: ApprovalOddsAnalytics;
  firePageActionOnClick?: boolean;
}) => {
  const {
    sectionPosition = 0,
    elementPosition = 0,
    sectionName = SECTION_NAME,
    parentActivityId
  } = analytics;
  const {
    deviceId,
    analytics: segmentAnalytics,
    deployableVersion
  } = useAnalytics();
  const {
    pageName,
    pageViewId,
    contentId,
    contentSource
  } = useAnalyticsPageView();
  const {
    skuImpressionId
  } = useAnalyticsSkuViewedContext();
  const {
    isLoggedIn
  } = useIdentityContext();
  const pageActionUuidRef = useRef(v4());
  const featureViewedUuidRef = useRef(v4());
  const openApprovalOdds = usePageAction({
    actionName: ENTITY_NAME,
    actionSection: sectionName,
    actionType: 'open',
    activityId: pageActionUuidRef.current,
    parentActivityId: featureViewedUuidRef.current,
    additionalAttributes: {
      sku_impression_id: skuImpressionId
    }
  });
  return <AnalyticsFeatureViewed entityName={ENTITY_NAME} entitySection={sectionName} impressionPosition={elementPosition || sectionPosition} activityId={featureViewedUuidRef.current} parentActivityId={parentActivityId} additionalAttributes={{
    sku_impression_id: skuImpressionId,
    target_flow: 'credit-score',
    cta_mode: 'modal',
    cta_format: 'link',
    cta_label: CTA_LABEL
  }}>
      <button type="button" className={approvalOddsButtonClass} onClick={async () => {
      if (isLoggedIn) {
        window.location.assign(DASHBOARD_PATH);
        return;
      }
      const currentURL = new URL(getCleanLocationHref());
      const urlWithoutSearchParams = `${currentURL.origin}${currentURL.pathname}`;
      const anonymousUserId = (await segmentAnalytics?.segmentInstance?.user())?.anonymousId() || '';
      const redirectTo = encodeURI(`${env.NEXT_PUBLIC_NW_URL}/home/register?flow=credit-score&redirect_uri=${urlWithoutSearchParams}`);
      const options: Parameters<typeof auth0.signUp>[0] = {
        anonymousUserId,
        cancelUrl: urlWithoutSearchParams,
        deployableName: SEGMENT_DEPLOYABLE_NAME,
        deployableVersion,
        driverContentId: contentId?.toString() ?? '',
        driverContentSource: contentSource?.toString() ?? '',
        driverElement: 'link',
        driverLocation: 'product_card',
        driverPageName: pageName,
        driverPageUrl: urlWithoutSearchParams,
        driverPageViewId: pageViewId,
        driverSubLocation: 'see_your_approval_odds',
        mostRecentTraditionalDeviceId: deviceId,
        redirectTo
      };
      if (firePageActionOnClick) {
        openApprovalOdds(null, () => {
          auth0.signUp(options);
        });
      } else {
        auth0.signUp(options);
      }
    }}>
        {CTA_LABEL}
      </button>
    </AnalyticsFeatureViewed>;
};
export const ApprovalOddsButton = t0 => {
  const $ = _c(10);
  const {
    analytics: t1,
    hideTooltip: t2,
    firePageActionOnClick
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const analytics = t3;
  const hideTooltip = t2 === undefined ? false : t2;
  let t4;
  if ($[2] !== analytics || $[3] !== firePageActionOnClick) {
    t4 = <ApprovalOddsButtonInner analytics={analytics} firePageActionOnClick={firePageActionOnClick} />;
    $[2] = analytics;
    $[3] = firePageActionOnClick;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== hideTooltip) {
    t5 = !hideTooltip && <InfoTooltip ariaLabel="More information about Recommended Credit Score" popupMaxWidth={235} text={<Typography variant="text.small" css={{
      color: "common.black"
    }}>{RECOMMENDED_CREDIT_TOOLTIP_TEXT}{" "}<Link href={`${env.NEXT_PUBLIC_NW_URL}/${RECOMMENDED_CREDIT_LEARN_MORE_PATH}`} underline="hover" target="_blank" rel="noopener">Read more.</Link></Typography>} />;
    $[5] = hideTooltip;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};